import React, { createContext, useContext, useEffect, ReactNode, useState, useCallback } from "react";
import { apiService } from "./apiService";
import { useAuth } from "../auth/authContext";

interface ApiContextType {
  apiService: typeof apiService;
  isApiAvailable: boolean;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export const useApiClient = (): ApiContextType => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApiClient must be used within an ApiClientProvider");
  }
  return context;
};

interface ApiClientProviderProps {
  children: ReactNode;
}

const HEALTH_CHECK_INTERVAL = 10000; // 10 seconds
const INITIAL_CHECK_TIMEOUT = 5000; // 5 seconds for initial check

const ServiceUnavailablePage: React.FC<{ onRetry: () => void; isChecking: boolean }> = ({ onRetry, isChecking }) => (
  <div style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    padding: "20px",
    textAlign: "center"
  }}>
    <img 
      src="/images/shareparkSquareLogoMapIcon.svg" 
      alt="SharePark" 
      style={{ width: "80px", height: "80px", marginBottom: "24px", opacity: 0.5 }}
    />
    <h1 style={{ 
      fontSize: "24px", 
      fontWeight: 600, 
      color: "#111827", 
      marginBottom: "12px" 
    }}>
      SharePark is Currently Unavailable
    </h1>
    <p style={{ 
      fontSize: "16px", 
      color: "#6b7280", 
      marginBottom: "24px",
      maxWidth: "400px"
    }}>
      We're having trouble connecting to our servers. Please check your internet connection or try again later.
    </p>
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <button
        onClick={onRetry}
        disabled={isChecking}
        style={{
          backgroundColor: isChecking ? "#d1d5db" : "#f97316",
          color: "white",
          border: "none",
          borderRadius: "8px",
          padding: "12px 24px",
          fontSize: "14px",
          fontWeight: 500,
          cursor: isChecking ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}
      >
        {isChecking ? (
          <>
            <span style={{
              width: "16px",
              height: "16px",
              border: "2px solid #ffffff",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }} />
            Checking...
          </>
        ) : (
          "Try Again"
        )}
      </button>
    </div>
    <p style={{ 
      fontSize: "12px", 
      color: "#9ca3af", 
      marginTop: "24px" 
    }}>
      Automatically retrying every 10 seconds...
    </p>
    <style>
      {`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}
    </style>
  </div>
);

export const ApiClientProvider: React.FC<ApiClientProviderProps> = ({
  children,
}) => {
  const { token } = useAuth();
  const [isApiAvailable, setIsApiAvailable] = useState<boolean | null>(null); // null = checking
  const [isChecking, setIsChecking] = useState(false);

  const checkApiHealth = useCallback(async (): Promise<boolean> => {
    try {
      const baseUrl = apiService.getBaseUrl();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), INITIAL_CHECK_TIMEOUT);
      
      const response = await fetch(`${baseUrl}/Health`, {
        method: "GET",
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.warn("API health check failed:", error);
      return false;
    }
  }, []);

  const performHealthCheck = useCallback(async () => {
    setIsChecking(true);
    const isAvailable = await checkApiHealth();
    setIsApiAvailable(isAvailable);
    setIsChecking(false);
    return isAvailable;
  }, [checkApiHealth]);

  // Initial health check
  useEffect(() => {
    performHealthCheck();
  }, [performHealthCheck]);

  // Recurring health check when API is unavailable
  useEffect(() => {
    if (isApiAvailable === false) {
      const intervalId = setInterval(() => {
        performHealthCheck();
      }, HEALTH_CHECK_INTERVAL);

      return () => clearInterval(intervalId);
    }
  }, [isApiAvailable, performHealthCheck]);

  useEffect(() => {
    // Update the API client's auth token whenever the auth token changes
    apiService.setAuthToken(token);
  }, [token]);

  const value: ApiContextType = {
    apiService,
    isApiAvailable: isApiAvailable ?? false,
  };

  // Show loading state during initial check
  if (isApiAvailable === null) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#f9fafb"
      }}>
        <div style={{
          width: "40px",
          height: "40px",
          border: "3px solid #e5e7eb",
          borderTopColor: "#f97316",
          borderRadius: "50%",
          animation: "spin 1s linear infinite"
        }} />
        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Show unavailable page if API is down
  if (!isApiAvailable) {
    return <ServiceUnavailablePage onRetry={performHealthCheck} isChecking={isChecking} />;
  }

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};
